import {
  Button,
  ConfirmModal,
  Input,
  InputDay,
  InputWrapper,
} from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { showSuccessNotification } from '@vrplatform/theme';
import { showWarnNotification } from '@vrplatform/theme';
import { type Maybe, day } from '@vrplatform/utils';
import { Box, Group, Modal } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Controller, useForm } from 'react-hook-form';

interface ModalProps {
  opened: boolean;
  closeModal: () => void;
  payout: Maybe<{
    id: string;
    date: string;
    description: string;
  }>;
}

export const PayoutEllipsisMenuModals = ({
  deleteModal,
  editModal,
  payout: initial,
}: {
  payout: ModalProps['payout'];
  deleteModal: {
    opened: boolean;
    closeModal: () => void;
  };
  editModal: {
    opened: boolean;
    closeModal: () => void;
  };
}) => {
  const [debounced] = useDebouncedValue(initial, 500);

  const payout = initial || debounced;

  return (
    <>
      <Delete {...deleteModal} payout={payout} />
      <Edit {...editModal} payout={payout} />
    </>
  );
};

const Delete = ({ opened, closeModal, payout }: ModalProps) => {
  const payoutId = payout?.id;
  const invalidate = useInvalidateApiQueries();

  const { mutateAsync: mutate, isPending: loading } = useApiMutation(
    'delete',
    '/transactions/{id}'
  );

  const submit = async () =>
    await mutate({
      params: {
        path: {
          id: payoutId!,
        },
        query: {
          onLocked: 'error',
        },
      },
    })
      .then(() => {
        invalidate(['owner-statements']);
        invalidate(['accounts']);
        closeModal();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to delete payout',
          defaultMessage:
            'We failed to delete the payout. Please try again and contact support if the issue persists.',
        });
      });

  return (
    <ConfirmModal
      type="delete"
      zIndex={1000}
      opened={opened}
      loading={loading}
      closeModal={closeModal}
      onSubmit={submit}
      title={'Delete this payout?'}
      subtitle={
        'Are you sure you want to delete this payout? This action cannot be undone.'
      }
    />
  );
};

type FormInputs = {
  date: string;
  description: string;
};

const Edit = ({ opened, closeModal, payout }: ModalProps) => {
  const invalidate = useInvalidateApiQueries(['owner-statements']);

  const methods = useForm<FormInputs>({
    values: payout
      ? {
          date: payout.date,
          description: payout.description,
        }
      : undefined,
  });

  const { mutateAsync: update } = useApiMutation(
    'put',
    '/transactions/{id}',
    {}
  );

  const submit = async (args: FormInputs) => {
    if (!payout)
      return showWarnNotification({
        message:
          'We failed to find the payout. Please reach out to support if the issue persists.',
      });

    try {
      const dirtyFields = methods.formState.dirtyFields;

      if (dirtyFields.date || dirtyFields.description) {
        await update({
          params: {
            path: {
              id: payout.id,
            },
          },
          body: {
            date: dirtyFields.date ? args.date : undefined,
            description: dirtyFields.description ? args.description : undefined,
            payment: dirtyFields.date
              ? {
                  date: args.date,
                }
              : undefined,
          },
        });
      }

      showSuccessNotification({
        title: 'Success!',
        message: 'Successfully updated payout.',
      });
      invalidate();
      closeModal();
    } catch (error) {
      showApiErrorNotification({
        error,
        title: 'Failed to update payout',
        defaultMessage:
          'We failed to update the payout. Please try again and contact support if the issue persists.',
      });
    }
  };

  return (
    <Modal
      centered
      onClose={closeModal}
      opened={opened}
      size={550}
      title="Edit payout"
    >
      <Box
        component="form"
        onReset={closeModal}
        onSubmit={methods.handleSubmit(submit)}
      >
        <Controller
          control={methods.control}
          name="description"
          rules={{
            validate: (value: string) =>
              !!value.trim() || 'Please enter a description',
          }}
          render={({ field, fieldState }) => (
            <InputWrapper mb="xs" label="Description">
              <Input
                error={fieldState.error?.message}
                value={field.value}
                setValue={field.onChange}
                placeholder={'Payout description'}
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={methods.control}
          name="date"
          rules={{
            required: 'Please enter a payout date',
          }}
          render={({ field, fieldState }) => (
            <InputWrapper mb="xl" label="Payout date">
              <InputDay
                error={fieldState.error?.message}
                value={field.value ? day(field.value).toDate() : null}
                onChange={(value) =>
                  field.onChange(value ? day(value).yyyymmdd() : null)
                }
                placeholder={day().format('ll')}
              />
            </InputWrapper>
          )}
        />

        <Group justify="flex-end">
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={methods.formState.isSubmitting}
          >
            Save changes
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
